import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { tokenManager } from '../../lib/api'
import { Users, UserCheck, Briefcase, Scale } from 'lucide-react'
import MultiSelect from '../../components/ui/MultiSelect'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface User {
  id: number
  name: string
  email: string
  role?: string
}

interface TeamData {
  pm: User | null
  reviewers: User[]
  legal_contacts: User[]
  contracting_officers: User[]
  technical_smes: User[]
  team_notes: string
}

interface TeamRecommendations {
  recommended_reviewer_count?: number
  recommended_legal_count?: number
  recommended_co_count?: number
  recommended_sme_count?: number
  reasoning?: {
    reviewers?: string
    legal?: string
    contracting_officers?: string
    technical_smes?: string
  }
  total_team_size?: number
}

const TeamView: React.FC = () => {
  const { programId } = useParams<{ programId: string }>()
  const [team, setTeam] = useState<TeamData | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [teamRecommendations, setTeamRecommendations] = useState<TeamRecommendations | null>(null)
  
  // Editable team state
  const [editableTeam, setEditableTeam] = useState({
    pm: null as number | null,
    reviewers: [] as number[],
    legal_contacts: [] as number[],
    contracting_officers: [] as number[]
  })

  useEffect(() => {
    const fetchData = async () => {
      if (!programId) return

      try {
        const token = tokenManager.getAccessToken()
        if (!token) return

        // Fetch available users (all users for now, we'll filter in the UI)
        const usersResponse = await fetch(`${API_BASE_URL}/api/users/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (usersResponse.ok) {
          const usersData = await usersResponse.json()
          setUsers(usersData || [])
        }

        // Fetch team data
        const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/team/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })

        if (response.ok) {
          const data = await response.json()
          setTeam(data)
          
          // Initialize editable state
          setEditableTeam({
            pm: data.pm?.id || null,
            reviewers: data.reviewers?.map((r: User) => r.id) || [],
            legal_contacts: data.legal_contacts?.map((l: User) => l.id) || [],
            contracting_officers: data.contracting_officers?.map((c: User) => c.id) || []
          })
        }
        
        // Fetch team recommendations
        const recommendationsResponse = await fetch(`${API_BASE_URL}/api/programs/${programId}/team-recommendations/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (recommendationsResponse.ok) {
          const recommendationsData = await recommendationsResponse.json()
          setTeamRecommendations(recommendationsData)
        }

        setLoading(false)
      } catch (err) {
        console.error('Error fetching team:', err)
        setLoading(false)
      }
    }

    fetchData()
  }, [programId])
  
  const handleSave = async () => {
    if (!editableTeam.pm) {
      alert('Please select a Program Manager')
      return
    }

    setSaving(true)
    try {
      const token = tokenManager.getAccessToken()
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/team/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editableTeam)
      })

      if (response.ok) {
        // Refresh team data
        const teamResponse = await fetch(`${API_BASE_URL}/api/programs/${programId}/team/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (teamResponse.ok) {
          const data = await teamResponse.json()
          setTeam(data)
        }
        setEditing(false)
      } else {
        alert('Failed to save team')
      }
    } catch (err) {
      console.error('Error saving team:', err)
      alert('An error occurred while saving team')
    } finally {
      setSaving(false)
    }
  }
  
  // Helper function to filter users by role
  const getUsersByRole = (role: string) => {
    return users.filter(u => u.role === role)
  }
  
  // Recommended counts come from /team-recommendations/. There is no second
  // source for the reviewer count since the pathway endpoint was retired, so a
  // missing recommendation renders no hint at all (each is gated on `> 0`).
  const recommendedReviewers = teamRecommendations?.recommended_reviewer_count || 0
  const recommendedLegal = teamRecommendations?.recommended_legal_count || 1
  const recommendedCOs = teamRecommendations?.recommended_co_count || 1

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Program Team
          </h1>
          <p className="text-muted-foreground">
            {editing ? 'Edit team members assigned to this program' : 'View and manage team members assigned to this program'}
          </p>
        </div>
        {team && (
          <button
            onClick={() => {
              if (editing) {
                // Cancel edit
                setEditableTeam({
                  pm: team.pm?.id || null,
                  reviewers: team.reviewers?.map((r: User) => r.id) || [],
                  legal_contacts: team.legal_contacts?.map((l: User) => l.id) || [],
                  contracting_officers: team.contracting_officers?.map((c: User) => c.id) || []
                })
                setEditing(false)
              } else {
                setEditing(true)
              }
            }}
            className="px-4 py-2 border border-input text-card-foreground rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {editing ? 'Cancel' : 'Edit Team'}
          </button>
        )}
      </div>
      
      {/* Team Recommendations */}
      {editing && teamRecommendations && Object.keys(teamRecommendations).length > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Team Recommendations</h3>
          <div className="space-y-1 text-sm text-blue-900 dark:text-blue-200">
            {teamRecommendations.recommended_reviewer_count && (
              <p>• <span className="font-semibold">{teamRecommendations.recommended_reviewer_count} Reviewers:</span> {teamRecommendations.reasoning?.reviewers || 'For thorough evaluation'}</p>
            )}
            {teamRecommendations.recommended_legal_count && (
              <p>• <span className="font-semibold">{teamRecommendations.recommended_legal_count} Legal:</span> {teamRecommendations.reasoning?.legal || 'For contract review'}</p>
            )}
            {teamRecommendations.recommended_co_count && (
              <p>• <span className="font-semibold">{teamRecommendations.recommended_co_count} Contracting Officer(s):</span> {teamRecommendations.reasoning?.contracting_officers || 'For contract execution'}</p>
            )}
            {teamRecommendations.recommended_sme_count && (
              <p>• <span className="font-semibold">{teamRecommendations.recommended_sme_count} Technical SME(s):</span> {teamRecommendations.reasoning?.technical_smes || 'For technical expertise'}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {editing ? (
          /* Edit Mode */
          <>
            {/* Program Manager */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">
                  Program Manager <span className="text-red-600">*</span>
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                The primary owner and decision-maker for this program.
              </p>
              <select
                value={editableTeam.pm || ''}
                onChange={(e) => setEditableTeam({ ...editableTeam, pm: Number(e.target.value) || null })}
                className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-900 text-foreground"
              >
                <option value="">Select Program Manager</option>
                {getUsersByRole('pm').length > 0 ? (
                  getUsersByRole('pm').map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))
                ) : (
                  users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))
                )}
              </select>
            </div>

            {/* Reviewers */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-lg font-semibold text-foreground">
                  Reviewers
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Technical reviewers who will evaluate vendor submissions.
                {recommendedReviewers > 0 && (
                  <span className="font-semibold text-primary"> Recommended: {recommendedReviewers}</span>
                )}
              </p>
              <MultiSelect
                options={getUsersByRole('reviewer').length > 0 ? getUsersByRole('reviewer') : users}
                selectedIds={editableTeam.reviewers}
                onChange={(ids) => setEditableTeam({ ...editableTeam, reviewers: ids })}
                placeholder="Select reviewers..."
              />
            </div>

            {/* Legal Contacts */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-5 h-5 text-success" />
                <h3 className="text-lg font-semibold text-foreground">
                  Legal Contacts
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Legal counsel for contract review and compliance.
                {recommendedLegal > 0 && (
                  <span className="font-semibold text-primary"> Recommended: {recommendedLegal}</span>
                )}
              </p>
              <MultiSelect
                options={getUsersByRole('legal').length > 0 ? getUsersByRole('legal') : users}
                selectedIds={editableTeam.legal_contacts}
                onChange={(ids) => setEditableTeam({ ...editableTeam, legal_contacts: ids })}
                placeholder="Select legal contacts..."
              />
            </div>

            {/* Contracting Officers */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <h3 className="text-lg font-semibold text-foreground">
                  Contracting Officers
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Contracting officers responsible for award execution.
                {recommendedCOs > 0 && (
                  <span className="font-semibold text-primary"> Recommended: {recommendedCOs}</span>
                )}
              </p>
              <MultiSelect
                options={getUsersByRole('contracting').length > 0 ? getUsersByRole('contracting') : users}
                selectedIds={editableTeam.contracting_officers}
                onChange={(ids) => setEditableTeam({ ...editableTeam, contracting_officers: ids })}
                placeholder="Select contracting officers..."
              />
            </div>
            
            {/* Save button for edit mode */}
            {editing && (
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEditing(false)}
                  className="px-6 py-2 border border-input text-card-foreground rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!editableTeam.pm || saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </>
        ) : (
          /* View Mode */
          <>
        {/* Program Manager */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <UserCheck className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              Program Manager
            </h2>
          </div>
          {team?.pm ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-blue-700 dark:text-blue-300 font-semibold">
                  {team.pm.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="font-medium text-foreground">{team.pm.name}</p>
                <p className="text-sm text-muted-foreground">{team.pm.email}</p>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground italic">No PM assigned</p>
          )}
        </div>

        {/* Reviewers */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-lg font-semibold text-foreground">
              Reviewers
            </h2>
            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full text-xs font-medium text-muted-foreground">
              {team?.reviewers.length || 0}
            </span>
          </div>
          {team?.reviewers && team.reviewers.length > 0 ? (
            <div className="space-y-2">
              {team.reviewers.map(user => (
                <div key={user.id} className="flex items-center gap-3 p-2">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <span className="text-purple-700 dark:text-purple-300 text-xs font-semibold">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground italic">No reviewers assigned</p>
          )}
        </div>

        {/* Legal Contacts */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-5 h-5 text-success" />
            <h2 className="text-lg font-semibold text-foreground">
              Legal Contacts
            </h2>
            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full text-xs font-medium text-muted-foreground">
              {team?.legal_contacts.length || 0}
            </span>
          </div>
          {team?.legal_contacts && team.legal_contacts.length > 0 ? (
            <div className="space-y-2">
              {team.legal_contacts.map(user => (
                <div key={user.id} className="flex items-center gap-3 p-2">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <span className="text-green-700 dark:text-green-300 text-xs font-semibold">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground italic">No legal contacts assigned</p>
          )}
        </div>

        {/* Contracting Officers */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h2 className="text-lg font-semibold text-foreground">
              Contracting Officers
            </h2>
            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full text-xs font-medium text-muted-foreground">
              {team?.contracting_officers.length || 0}
            </span>
          </div>
          {team?.contracting_officers && team.contracting_officers.length > 0 ? (
            <div className="space-y-2">
              {team.contracting_officers.map(user => (
                <div key={user.id} className="flex items-center gap-3 p-2">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                    <span className="text-orange-700 dark:text-orange-300 text-xs font-semibold">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground italic">No contracting officers assigned</p>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TeamView

